import { supabase } from './supabase';
import { logger } from './logger';

export interface AdminVideo {
  id: string;
  candidateId: string;
  candidateName: string;
  videoUrl: string;
  thumbnailUrl: string | null;
  durationSeconds: number | null;
  /** 'video' | 'image' — un post image se prévisualise en <img>, pas en <video> */
  mediaType: 'video' | 'image';
  textOverlay: string | null;
  status: 'pending_review' | 'approved' | 'rejected';
  rejectionReason: string | null;
  reviewedAt: string | null;
  submittedAt: string;
  tags: string[] | null;
}

export async function fetchVideos(): Promise<AdminVideo[]> {
  const { data, error } = await supabase
    .from('candidate_videos')
    .select(`
      id,
      candidate_id,
      video_url,
      thumbnail_url,
      duration_seconds,
      media_type,
      text_overlay,
      status,
      rejection_reason,
      reviewed_at,
      created_at,
      tags,
      candidate_profiles!candidate_videos_candidate_id_fkey ( first_name, last_name )
    `)
    .order('created_at', { ascending: false });

  if (error) {
    logger.error('fetch_videos', 'videoService', { error: error.message });
    throw error;
  }
  logger.technical('fetch_videos_ok', 'videoService', { count: (data || []).length });

  return (data || []).map((v: any) => ({
    id: v.id,
    candidateId: v.candidate_id,
    candidateName: `${v.candidate_profiles?.first_name || ''} ${v.candidate_profiles?.last_name || ''}`.trim() || 'Inconnu',
    videoUrl: v.video_url,
    thumbnailUrl: v.thumbnail_url,
    durationSeconds: v.duration_seconds,
    mediaType: v.media_type === 'image' ? 'image' : 'video',
    textOverlay: v.text_overlay || null,
    status: v.status,
    rejectionReason: v.rejection_reason,
    reviewedAt: v.reviewed_at ? new Date(v.reviewed_at).toLocaleDateString('fr-FR') : null,
    submittedAt: new Date(v.created_at).toLocaleString('fr-FR'),
    tags: v.tags,
  }));
}

export async function approveVideo(videoId: string, adminId: string): Promise<void> {
  logger.functional('approve_video', 'videoService', { videoId, adminId });
  const { error } = await supabase
    .from('candidate_videos')
    .update({
      status: 'approved',
      reviewed_by: adminId,
      reviewed_at: new Date().toISOString(),
    })
    .eq('id', videoId);

  if (error) {
    logger.error('approve_video', 'videoService', { videoId, error: error.message });
    throw error;
  }
  logger.technical('approve_video_ok', 'videoService', { videoId });
}

export async function rejectVideo(videoId: string, adminId: string, reason: string): Promise<void> {
  logger.functional('reject_video', 'videoService', { videoId, adminId, reason });
  const { error } = await supabase
    .from('candidate_videos')
    .update({
      status: 'rejected',
      reviewed_by: adminId,
      reviewed_at: new Date().toISOString(),
      rejection_reason: reason,
    })
    .eq('id', videoId);

  if (error) {
    logger.error('reject_video', 'videoService', { videoId, error: error.message });
    throw error;
  }
  logger.technical('reject_video_ok', 'videoService', { videoId });
}

export async function unpublishVideo(videoId: string, adminId: string): Promise<void> {
  logger.functional('unpublish_video', 'videoService', { videoId, adminId });
  const { error } = await supabase
    .from('candidate_videos')
    .update({
      status: 'pending_review',
      reviewed_by: adminId,
      reviewed_at: new Date().toISOString(),
      rejection_reason: null,
    })
    .eq('id', videoId);

  if (error) {
    logger.error('unpublish_video', 'videoService', { videoId, error: error.message });
    throw error;
  }
  logger.technical('unpublish_video_ok', 'videoService', { videoId });
}
